import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useBusinessAuthContext } from "../commoncomponents/useBusinessAuthContext";
import API_URL from "../../api/API";
import Sidebar from "../../components/Sidebar";

const WaiverSignatures = () => {

  const { business } = useBusinessAuthContext();
  const navigate = useNavigate();
  const { waiverId } = useParams();

  const [searchParams, setSearchParams] =
    useSearchParams();

  const range = searchParams.get("range");
  const isBusinessWide =
    !waiverId && (!range || ["today", "7days"].includes(range));

  const versionMode =
    searchParams.get("versions") === "all"
      ? "all"
      : "current";

  const [signatures, setSignatures] =
    useState([]);

  const [waiver, setWaiver] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  useEffect(() => {

    if (!business) {
      navigate("/business/login");
      return;
    }

    const fetchSignatures = async () => {

      setLoading(true);
      setError("");

      try {

        const page = 1;
        const limit = 20;

        const url = isBusinessWide
          ? `${API_URL.BUSINESS_WAIVER_SIGNATURES}?range=${range}`
          : `${API_URL.BUSINESS_WAIVER_SIGNATURES}/${waiverId}?versions=${versionMode}`;

        const headers = {
          Authorization: `Bearer ${business.token}`,
          "x-session-id": business.sessionId,
          "x-frontend-url": window.location.pathname,
        };  

        const res = await fetch(
          url,
          { headers }
        );

        const data = await res.json();

        if (!data.success) {
          throw new Error(
            data.message ||
            "Failed to load signatures"
          );
        }

        setWaiver(data.waiver);
        setSignatures(data.signatures || []);

      } catch (err) {

        console.error(
          "Failed to fetch signatures:",
          err
        );

        setError(
          err.message ||
          "Failed to load signatures"
        );

      } finally {

        setLoading(false);
      }
    };

    fetchSignatures();

  }, [
    business,
    navigate,
    waiverId,
    versionMode,
    range,
    isBusinessWide,
  ]);

  const handleVersionMode = (mode) => {

    setSearchParams({
      versions: mode,
    });
  };

  const formatDate = (date) => {

    if (!date) {
      return "-";
    }

    return new Date(date).toLocaleString(
      undefined,
      {
        dateStyle: "medium",
        timeStyle: "short",
      }
    );
  };

  const handlePDFView = async (signature) => {

    if (!signature?.signedPdfId) {
      return;
    }

    try {

      const res = await fetch(
        `${API_URL.BUSINESS_PDF_VIEW}/${signature.signedPdfId}`,
        {
          headers: {
            Authorization:
              `Bearer ${business.token}`,

            "x-session-id":
              business.sessionId,

            "x-frontend-url":
              window.location.pathname,
          },
        }
      );

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(
          data.message ||
          "Failed to generate PDF download link"
        );
      }

      if (!data.pdf?.url) {
        throw new Error(
          "PDF download URL was not returned"
        );
      }

      /*
      * Open the temporary signed URL.
      *
      * The URL is generated by the backend and
      * expires after 15 minutes.
      */
      window.open(
        data.pdf.url,
        "_blank",
        "noopener,noreferrer"
      );

    } catch (err) {

      console.error(
        "Failed to download signed PDF:",
        err
      );

      alert(
        err.message ||
        "Failed to download signed PDF"
      );
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-row bg-background">
        <Sidebar />
        <div className="flex-1 overflow-auto py-9">
          <div className="max-w-6xl mx-auto px-6">
            Loading signatures...
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="h-screen flex flex-row bg-background">
        <Sidebar />
        <div className="flex-1 overflow-auto py-9">
          <div className="max-w-6xl mx-auto px-6 text-red-600">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
  <div className="h-screen flex flex-row bg-background">
    <Sidebar />

    <div className="flex-1 h-auto overflow-visible py-9 transition">
      <div className="flex-1 overflow-visible py-9">
        <div className="max-w-6xl mx-auto px-6">

      {/* Header */}

      <div className="mb-8">


      <button
        onClick={() =>
          navigate(
            isBusinessWide
              ? "/business/dashboard"
              : waiver?.waiverStatus === "published"
                ? `/business/active-waiver/${waiverId}`
                : `/business/waiverviewer/${waiverId}/version-history`
          )
        }
        className="text-sm text-gray-500 hover:text-black mb-4"
      >
        ←{" "}
        {isBusinessWide
          ? "Back to Dashboard"
          : waiver?.waiverStatus === "published"
            ? "Back to Active Waiver"
            : "Back to View Waiver Versions"}
      </button>


        <div className="flex justify-between items-start gap-4">

          <div>

            <h1 className="text-2xl font-semibold">
              {isBusinessWide
                ? "Business Signatures"
                : "Waiver Signatures"}
            </h1>

            <p className="text-sm text-muted-foreground mt-1">
              {isBusinessWide
                ? range === "today"
                  ? "Signatures from today across all waivers"
                  : ""
                : "View customers who have signed this waiver"}
            </p>

          </div>

          {!isBusinessWide && waiver?.waiverStatus === "published" && (

            <div className="flex gap-2">

              <button
                onClick={() =>
                  handleVersionMode("all")
                }
                className={`px-4 py-2 rounded-full text-sm ${
                  versionMode === "all"
                    ? "bg-black text-white"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                View all versions
              </button>

              <button
                onClick={() =>
                  handleVersionMode("current")
                }
                className={`px-4 py-2 rounded-full text-sm ${
                  versionMode === "current"
                    ? "bg-black text-white"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                Current version only
              </button>

            </div>

          )}

        </div>

      </div>


      {/* Summary */}

      <div className="mb-5">

        <div className="text-sm text-gray-500">
          {isBusinessWide
            ? range === "today"
              ? "Signatures across all waivers today"
              : range === "7days"
                ? "Signatures across all waivers from the last 7 days"
                : "Signatures"
            : versionMode === "all"
              ? "Signatures from all waiver versions"
              : `Signatures for version ${waiver?.version || ""}`}
        </div>

        <div className="text-lg font-medium mt-1">
          {signatures.length} signature
          {signatures.length === 1
            ? ""
            : "s"}
        </div>

      </div>


      {/* Table */}

      {signatures.length === 0 ? (

        <div className="border rounded-xl p-10 text-center text-gray-500">
          No signatures found for this waiver.
        </div>

      ) : (

        <div className="border rounded-xl overflow-hidden">

          <div className="overflow-x-auto">

            <table className="w-full text-sm">

              <thead className="bg-gray-50 border-b">

                <tr>

                  <th className="text-left px-5 py-4 font-medium">
                    Signer name
                  </th>

                  <th className="text-left px-5 py-4 font-medium">
                    Is minor
                  </th>

                  <th className="text-left px-5 py-4 font-medium">
                    Minor's name
                  </th>

                  <th className="text-left px-5 py-4 font-medium">
                    Signed date
                  </th>

                  <th className="text-left px-5 py-4 font-medium">
                    Waiver
                  </th>

                  <th className="text-left px-5 py-4 font-medium">
                    Waiver version
                  </th>

                  <th className="text-left px-5 py-4 font-medium">
                    Signed PDF
                  </th>

                </tr>

              </thead>

              <tbody className="divide-y">

                {signatures.map((signature) => (

                  <tr
                    key={signature.id}
                    className="hover:bg-gray-50"
                  >

                    <td className="px-5 py-4 font-medium">
                      {signature.signerName || "-"}
                    </td>

                    <td className="px-5 py-4">

                      {signature.isMinor ? (
                        <span className="px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-800 text-xs">
                          Yes
                        </span>
                      ) : (
                        <span className="text-gray-600">
                          No
                        </span>
                      )}

                    </td>

                    <td className="px-5 py-4">
                      {signature.minorName || "-"}
                    </td>

                    <td className="px-5 py-4 whitespace-nowrap">
                      {formatDate(
                        signature.signedDate
                      )}
                    </td>

                    
                    
                  <td className="px-5 py-4">
                    {signature.waiver?.name || "Waiver"}
                  </td>


                    <td className="px-5 py-4">
                      {signature.waiverVersion
                        ? `v${signature.waiverVersion}`
                        : "-"}
                    </td>

                    <td className="px-5 py-4">

                      {signature.signedPdfId ? (

                        <button
                          onClick={() =>
                            handlePDFView(
                              signature
                            )
                          }
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          View PDF
                        </button>

                      ) : (

                        <span className="text-gray-400">
                          Not available
                        </span>

                      )}

                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

          </div>

        </div>

      )}

    </div>        
        </div>
      </div>
    </div>
  );
};

export default WaiverSignatures;